import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/1c2d2ee2/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import DataOptionsSection from './shared-data-options-section';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const InlineNotification = makeShortcode("InlineNotification");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const ResourceCard = makeShortcode("ResourceCard");
const MdxIcon = makeShortcode("MdxIcon");
const Title = makeShortcode("Title");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <PageDescription mdxType="PageDescription">
      <p>{`Carbon charts help you tell accurate and convincing stories around data with beautiful and accessible visualizations.`}</p>
    </PageDescription>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Please direct all questions regarding support, bug fixes, and feature requests to the `}<a parentName="p" {...{
          "href": "https://github.com/carbon-design-system/carbon-charts#core-team"
        }}>{`Carbon Charts core team`}</a>{`.`}</p>
    </InlineNotification>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Carbon Charts Vanilla library</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Install</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Getting started</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Development</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Troubleshooting</AnchorLink>
    </AnchorLinks>
    <h2>{`Resources`}</h2>
    <Row className="resource-card-group" mdxType="Row">
      <Column colMd={4} colLg={4} noGutterSm mdxType="Column">
  <ResourceCard subTitle="Carbon Charts Core" href="https://github.com/carbon-design-system/carbon-charts/tree/master/packages/core" mdxType="ResourceCard">
    <MdxIcon name="github" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
      <Column colMd={4} colLg={4} noGutterSm mdxType="Column">
  <ResourceCard subTitle="Try Carbon Charts Vanilla components with CodeSandbox." href="https://codesandbox.io/s/carbon-charts-vanilla-m9r77" mdxType="ResourceCard">
    <MdxIcon name="codesandbox" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
      <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
  <ResourceCard subTitle="Download WIP charting design specifications (Coming soon)" disabled mdxType="ResourceCard">
    <MdxIcon name="sketch" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
      <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
        <ResourceCard subTitle="Data visualization color palettes (IBM internal link)" href="https://ibm.box.com/s/3mt8fshtgy70r7rhxjhfo3gbdt0n3x5y" actionIcon="download" mdxType="ResourceCard">
    <MdxIcon name="sketch" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
    </Row>
    <h2>{`Carbon Charts Vanilla library`}</h2>
    <p>{`The library provides a collection of reusable charting components to build websites and user interfaces. Adopting the library enables developers to use consistent markup, styles, and behavior in prototype and production work.`}</p>
    <h2>{`Install`}</h2>
    <Title mdxType="Title"> If using yarn: </Title>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`$ yarn add @carbon/charts d3
`}</code></pre>
    <Title mdxType="Title"> If you prefer npm: </Title>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`$ npm install --save @carbon/charts d3
`}</code></pre>
    <h2>{`Getting started`}</h2>
    <Title mdxType="Title"> If consuming through a bundler (e.g. webpack): </Title>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=index.html",
        "path": "index.html"
      }}>{`...
<div id="my-bar-chart"></div>
...
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "path=index.js",
        "path": "index.js"
      }}>{`import '@carbon/charts/styles.css';
import { StackedBarChart } from '@carbon/charts';

// grab chart holder DOM element
const chartHolder = document.getElementById('my-bar-chart');

// initialize the chart
new StackedBarChart(chartHolder, {
  data: stackedBarData,
  options: stackedBarOptions,
});
`}</code></pre>
    <Title mdxType="Title"> If consuming in a browser environment (e.g. through CDNs): </Title>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=index.html",
        "path": "index.html"
      }}>{`<!DOCTYPE html>
<html>
  <head>
    <link rel="stylesheet" href="https://unpkg.com/@carbon/charts/styles.css" />
  </head>
  <body>
    <div id="my-bar-chart"></div>

    <script src="https://unpkg.com/@carbon/charts/bundle.js"></script>
    <script>
      // grab chart holder DOM element
      const chartHolder = document.getElementById('my-bar-chart');

      const stackedBarData = {
        // ...see next section
      };

      const stackedBarOptions = {
        // ...see next section
      };

      // initialize the chart
      new Charts.StackedBarChart(chartHolder, {
        data: stackedBarData,
        options: stackedBarOptions,
      });
    </script>
  </body>
</html>
`}</code></pre>
    <DataOptionsSection mdxType="DataOptionsSection" />
    <h2>{`Demos and Examples`}</h2>
    <ul>
      <li parentName="ul"><strong parentName="li"><a parentName="strong" {...{
            "href": "https://carbon-design-system.github.io/carbon-charts/"
          }}>{`Demos - Webpage`}</a></strong></li>
      <li parentName="ul"><strong parentName="li"><a parentName="strong" {...{
            "href": "https://github.com/carbon-design-system/carbon-charts/tree/master/packages/core/demo/demo-data"
          }}>{`Demos - Data & options used`}</a></strong></li>
    </ul>
    <h2>{`Development`}</h2>
    <p>{`Please refer to the `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-charts/blob/master/CONTRIBUTING.md"
      }}>{`Contribution Guidelines`}</a>{` before starting any work.`}</p>
    <h3>{`Using the server`}</h3>
    <p>{`We recommend the use of `}<a parentName="p" {...{
        "href": "https://github.com/webpack/webpack-dev-server"
      }}>{`Webpack Dev Server`}</a>{` for developing components.`}</p>
    <Title mdxType="Title"> Start the server </Title>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`cd packages/core
yarn run demo:server
`}</code></pre>
    <p>{`Open browser to `}<inlineCode parentName="p">{`http://localhost:9001/`}</inlineCode>{`.`}</p>
    <h3>{`List of available components`}</h3>
    <p>{`View available components `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-charts#component-status"
      }}>{`here`}</a>{`.`}</p>
    <h2>{`Troubleshooting`}</h2>
    <p>{`If you experience any issues while getting set up with Carbon Charts, please head over to the `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-charts"
      }}>{`GitHub repo`}</a>{` for more guidelines and support. Please `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-charts/issues"
      }}>{`create an issue`}</a>{` if your issue does not already exist.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      